import { useState } from "react";
import { toast } from "react-toastify";
import styles from "./styles.module.scss";
import Modal from "../../../../../components/common/Modal";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import OutlinedButton from "../../../../../components/common/OutlinedButton";
import Spinner from "../../../../../components/common/Spinner";
import Table from "../../../../contract/list_v2/Table";
import Money from "../../../../contract/list_v2/Table/components/Money";
import { settleLockerShipments } from "../../../../../setup/api/services/lockerShipment";
import {
  IMatchLockerShipmentsData,
  ISettleLockerShipmentsData,
} from "../../../../../setup/api/services/lockerShipment/models";
import { formatAmount } from "../../../../../setup/util/wallet";
import { getApiErrorMessage } from "./helpers";
import { contractColumns, contractColumnVisibility } from "./columns";
import SettlementResult from "./SettlementResult";

interface Props {
  /** Result of the Excel match; the modal is shown while it's set. */
  result?: IMatchLockerShipmentsData;
  closeModal: () => void;
  onUploadAnother: () => void;
}

const MatchedShipmentsModal = ({
  result,
  closeModal,
  onUploadAnother,
}: Props) => {
  const [settling, setSettling] = useState(false);
  // Set once the settle call succeeds; switches the modal to the result step.
  const [settlement, setSettlement] = useState<ISettleLockerShipmentsData>();

  const matched = result?.matchedContracts || [];
  const unmatched = result?.unmatchedTrackingNumbers || [];
  const totalShipmentFees = matched.reduce(
    (sum, c) => sum + (c.shipmentFee || 0),
    0
  );

  const handleClose = () => {
    if (settling) return;
    setSettlement(undefined);
    closeModal();
  };

  // All matched contracts are settled together — the admin can't exclude any.
  const handleSettle = async () => {
    setSettling(true);
    try {
      const response = await settleLockerShipments(matched.map((c) => c.id));
      setSettlement({
        settledCount: response.data?.settledCount ?? 0,
        settledContractIds: response.data?.settledContractIds || [],
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "تعذر تسوية المعاملات"));
    } finally {
      setSettling(false);
    }
  };

  return (
    <Modal
      size="medium"
      visible={!!result}
      onCancel={handleClose}
      disableMaskClose={settling}>
      {settlement ? (
        <SettlementResult
          matched={matched}
          settlement={settlement}
          onClose={handleClose}
          onUploadAnother={() => {
            handleClose();
            onUploadAnother();
          }}
        />
      ) : (
        <div className={styles.modalContent}>
          <h2 className={styles.title}>نتيجة مطابقة الشحنات</h2>

          <div className={styles.summary}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>معاملات مطابقة</span>
              <span className={styles.summaryValue}>{matched.length}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>أرقام تتبع غير مطابقة</span>
              <span className={styles.summaryValue}>{unmatched.length}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>إجمالي رسوم الشحن</span>
              <Money amount={formatAmount(totalShipmentFees)} />
            </div>
          </div>

          <h3 className={styles.sectionTitle}>المعاملات المطابقة</h3>
          {matched.length > 0 ? (
            <div className={styles.tableScroll}>
              <Table
                columns={contractColumns}
                columnVisibility={contractColumnVisibility}
                data={matched}
              />
            </div>
          ) : (
            <p className={styles.empty}>
              لا توجد معاملات مستحقة لرسوم الشحن مطابقة لأرقام التتبع في الملف
            </p>
          )}

          {unmatched.length > 0 && (
            <>
              <h3 className={styles.sectionTitle}>أرقام التتبع غير المطابقة</h3>
              <div className={styles.chips}>
                {unmatched.map((trackingNumber, index) => (
                  <span
                    key={`${trackingNumber}-${index}`}
                    className={styles.chip}
                    dir="ltr">
                    {trackingNumber}
                  </span>
                ))}
              </div>
            </>
          )}

          <div className={styles.buttonContainer}>
            <PrimaryButton
              width="220px"
              onClick={handleSettle}
              disabled={settling || matched.length === 0}>
              {settling ? <Spinner /> : `تسوية الكل (${matched.length})`}
            </PrimaryButton>
            <OutlinedButton onClick={handleClose} disabled={settling}>
              إلغاء
            </OutlinedButton>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MatchedShipmentsModal;
